import { useState } from "react";
import { Button } from "./button";
import { Input } from "./input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";
import { Search } from "lucide-react";
import * as Icons from "lucide-react";

interface IconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
  trigger?: React.ReactNode;
}

// Categorias de ícones
const iconCategories = {
  "Geral": [
    "Home", "Star", "Heart", "Bell", "Flag", "Bookmark", "Award", "Trophy",
    "Target", "Zap", "Sparkles", "Crown", "Gift", "Tag", "Tags"
  ],
  "Negócios": [
    "TrendingUp", "TrendingDown", "BarChart3", "LineChart", "PieChart",
    "Activity", "DollarSign", "CreditCard", "Wallet", "Briefcase",
    "Calculator", "Receipt", "FileText", "Clipboard", "ClipboardList"
  ],
  "Compras": [
    "ShoppingCart", "ShoppingBag", "Package", "PackageCheck", "Truck",
    "Store", "DollarSign", "Receipt", "ShoppingBasket", "Coins"
  ],
  "Pessoas": [
    "User", "Users", "UserCheck", "UserPlus", "UserMinus", "UserCog",
    "Contact", "BadgeHelp", "PersonStanding", "UserCircle", "Shield"
  ],
  "Comunicação": [
    "Mail", "Send", "MessageSquare", "MessageCircle", "Phone", "Video",
    "Mic", "Bell", "BellRing", "Podcast", "Volume2", "Radio"
  ],
  "Arquivos": [
    "File", "FileText", "FilePlus", "FileCheck", "Folder", "FolderOpen",
    "Archive", "Download", "Upload", "Paperclip", "Link", "FileArchive"
  ],
  "Mídia": [
    "Image", "Play", "Pause", "Music", "Video", "Camera", "Film",
    "Mic", "Disc", "Radio", "PlayCircle", "Share2", "Heart"
  ],
  "Tecnologia": [
    "Laptop", "Smartphone", "Tablet", "Monitor", "Cpu", "HardDrive",
    "Database", "Server", "Wifi", "Bluetooth", "Usb", "Printer"
  ],
  "Tempo": [
    "Clock", "Calendar", "CalendarDays", "Timer", "AlarmClock", "Clock4",
    "Hourglass", "Sun", "Moon", "Sunrise", "Sunset", "CloudRain"
  ],
  "Localização": [
    "MapPin", "Map", "Navigation", "Compass", "Globe", "Building",
    "Building2", "Home", "Store", "GraduationCap", "Bed", "Cross"
  ],
  "Alimentos": [
    "Coffee", "Pizza", "Ham", "Apple", "Cherry", "Salad",
    "Wine", "Beer", "Droplet", "Utensils", "CookingPot", "Beef"
  ],
  "Transporte": [
    "Car", "Truck", "Bus", "Train", "Plane", "Ship", "Bike",
    "Rocket", "TruckIcon", "Fuel", "CircleParking", "TramFront"
  ],
  "Ações": [
    "Plus", "Minus", "X", "Check", "ChevronUp", "ChevronDown",
    "ChevronLeft", "ChevronRight", "ArrowUp", "ArrowDown", "ArrowLeft",
    "ArrowRight", "RefreshCw", "RotateCw", "Edit", "Trash2", "Save",
    "Download", "Upload", "Share2", "Copy", "Scissors", "Search"
  ],
  "Status": [
    "CheckCircle", "XCircle", "AlertCircle", "AlertTriangle", "Info",
    "HelpCircle", "Loader", "Lock", "Unlock", "Eye", "EyeOff", "ShieldCheck"
  ]
};

export default function IconPicker({ value, onChange, trigger }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Geral");

  // Filtrar ícones com base na pesquisa
  const getFilteredIcons = (category: string) => {
    const categoryIcons = iconCategories[category as keyof typeof iconCategories] || [];
    if (!search) return categoryIcons;
    
    return categoryIcons.filter(icon => 
      icon.toLowerCase().includes(search.toLowerCase())
    );
  };

  // Buscar em todas as categorias quando há pesquisa
  const getAllFilteredIcons = () => {
    if (!search) return [];
    
    const allIcons: string[] = [];
    Object.values(iconCategories).forEach(icons => {
      icons.forEach(icon => {
        if (icon.toLowerCase().includes(search.toLowerCase()) && !allIcons.includes(icon)) {
          allIcons.push(icon);
        }
      });
    });
    return allIcons;
  };

  const handleIconSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
    setSearch("");
  };

  // Obter o ícone atual - validar se existe
  const CurrentIcon = value && Icons[value as keyof typeof Icons]
    ? (Icons[value as keyof typeof Icons] as any)
    : Icons.Smile;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" className="w-full justify-start">
            <CurrentIcon className="w-4 h-4 mr-2" />
            {value || "Selecione um ícone"}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Selecione um Ícone</DialogTitle>
          <DialogDescription>
            Escolha um ícone para representar seu item
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar ícones..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {search ? (
            // Mostrar resultados da busca
            <div className="h-[400px] overflow-y-auto">
              <div className="grid grid-cols-8 gap-2 p-2">
                {getAllFilteredIcons().map((iconName) => {
                  const IconComponent = Icons[iconName as keyof typeof Icons] as any;
                  // Não renderizar se o ícone não existir
                  if (!IconComponent) return null;
                  
                  return (
                    <button
                      key={iconName}
                      onClick={() => handleIconSelect(iconName)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all hover:border-primary hover:bg-accent ${
                        value === iconName
                          ? "border-primary bg-primary/10"
                          : "border-border"
                      }`}
                      title={iconName}
                    >
                      <IconComponent className="w-5 h-5" />
                      <span className="text-[9px] mt-1 text-center leading-tight">
                        {iconName}
                      </span>
                    </button>
                  );
                })}
              </div>
              {getAllFilteredIcons().length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum ícone encontrado
                </div>
              )}
            </div>
          ) : (
            // Mostrar categorias
            <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
              <TabsList className="grid grid-cols-4 h-auto">
                {Object.keys(iconCategories).slice(0, 4).map((category) => (
                  <TabsTrigger key={category} value={category} className="text-xs">
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsList className="grid grid-cols-4 h-auto mt-1">
                {Object.keys(iconCategories).slice(4, 8).map((category) => (
                  <TabsTrigger key={category} value={category} className="text-xs">
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsList className="grid grid-cols-4 h-auto mt-1">
                {Object.keys(iconCategories).slice(8, 12).map((category) => (
                  <TabsTrigger key={category} value={category} className="text-xs">
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsList className="grid grid-cols-3 h-auto mt-1">
                {Object.keys(iconCategories).slice(12).map((category) => (
                  <TabsTrigger key={category} value={category} className="text-xs">
                    {category}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.keys(iconCategories).map((category) => (
                <TabsContent key={category} value={category} className="mt-4">
                  <div className="h-[280px] overflow-y-auto">
                    <div className="grid grid-cols-8 gap-2 p-2">
                      {getFilteredIcons(category).map((iconName) => {
                        const IconComponent = Icons[iconName as keyof typeof Icons] as any;
                        // Não renderizar se o ícone não existir
                        if (!IconComponent) return null;
                        
                        return (
                          <button
                            key={iconName}
                            onClick={() => handleIconSelect(iconName)}
                            className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all hover:border-primary hover:bg-accent ${
                              value === iconName
                                ? "border-primary bg-primary/10"
                                : "border-border"
                            }`}
                            title={iconName}
                          >
                            <IconComponent className="w-5 h-5" />
                            <span className="text-[9px] mt-1 text-center leading-tight">
                              {iconName}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
